import React, { useState, useEffect } from 'react';
import {
  Rocket,
  Code,
  Folder,
  GitBranch,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Loader2,
  CheckCircle,
  Book,
  Layers,
  Zap,
  RefreshCw,
  Target,
  ArrowRight,
  Lock,
  Star,
  TrendingUp,
  Sparkles
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { TechCard, TechButton, TechBadge } from './TechUI';
import { careerAPI } from '../utils/api';
import { toast } from 'react-hot-toast';
import UpgradePrompt from './UpgradePrompt';

const PortfolioBuilder = ({ userId, roadmaps: propRoadmaps, subscription }) => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [selectedCareerPath, setSelectedCareerPath] = useState('');
  const [roadmaps, setRoadmaps] = useState(propRoadmaps || []);
  const [projects, setProjects] = useState([]);
  const [savedPortfolios, setSavedPortfolios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingRoadmaps, setLoadingRoadmaps] = useState(!propRoadmaps);
  const [loadingSaved, setLoadingSaved] = useState(true);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [limitError, setLimitError] = useState(null);
  const [showGenerator, setShowGenerator] = useState(false);

  // Check if user has professional or enterprise tier
  const userTier = subscription?.tier || 'free';
  const isProfessional = ['professional', 'ultimate', 'enterprise'].includes(userTier);

  useEffect(() => {
    // Load user profile from localStorage
    const savedProfile = localStorage.getItem('userProfile');
    if (savedProfile) {
      setProfile(JSON.parse(savedProfile));
    }

    // Only fetch saved portfolios if user has access
    if (isProfessional) {
      loadSavedPortfolios();
    } else {
      setLoadingSaved(false);
    }

    // Fetch roadmaps from API if not provided via props
    if (!propRoadmaps) {
      fetchRoadmaps();
    } else {
      setRoadmaps(propRoadmaps);
      if (propRoadmaps.length > 0) {
        setSelectedCareerPath(propRoadmaps[0].careerPath);
      }
    }
  }, [propRoadmaps, isProfessional]);

  const loadSavedPortfolios = async () => {
    try {
      setLoadingSaved(true);
      const response = await careerAPI.getUserPortfolioProjects();
      setSavedPortfolios(response.portfolios || []);
    } catch (error) {
      console.error('Error loading saved portfolios:', error);
    } finally {
      setLoadingSaved(false);
    }
  };

  const fetchRoadmaps = async () => {
    try {
      setLoadingRoadmaps(true);
      const response = await careerAPI.getUserRoadmaps();
      const userRoadmaps = response.roadmaps || [];
      setRoadmaps(userRoadmaps);
      if (userRoadmaps.length > 0) {
        setSelectedCareerPath(userRoadmaps[0].careerPath);
      }
    } catch (error) {
      console.error('Error loading roadmaps:', error);
    } finally {
      setLoadingRoadmaps(false);
    }
  };

  const handleGenerateProjects = async () => {
    if (!selectedCareerPath) {
      toast.error('Please select a roadmap first');
      return;
    }

    setLoading(true);
    try {
      // Use profile if available, otherwise use empty object
      const userProfile = profile || {};
      const response = await careerAPI.generatePortfolioProjects(userProfile, selectedCareerPath);
      setProjects(response.projects || response);
      setLimitError(null);
      setShowGenerator(false);
      // Reload saved portfolios to show the new one
      await loadSavedPortfolios();
      toast.success(`Generated ${response.projects?.length || response.length} portfolio projects!`);
    } catch (error) {
      console.error('Error generating projects:', error);
      if (error.isLimitError && error.limitInfo) {
        setLimitError(error.limitInfo);
      } else {
        toast.error(error.message || 'Failed to generate portfolio projects');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePortfolio = async (portfolioId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this portfolio?')) return;

    try {
      await careerAPI.deletePortfolioProject(portfolioId);
      toast.success('Portfolio deleted');
      await loadSavedPortfolios();
    } catch (error) {
      toast.error('Failed to delete portfolio');
    }
  };

  const handleViewPortfolio = (portfolio) => {
    setProjects(portfolio.projects || []);
    setSelectedCareerPath(portfolio.careerPath);
    setShowGenerator(false);
  };


  const toggleProject = (index) => {
    setExpandedProjects(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'beginner':
        return 'text-green-400 bg-green-900/30 border-green-700';
      case 'intermediate':
        return 'text-yellow-400 bg-yellow-900/30 border-yellow-700';
      case 'advanced':
        return 'text-red-400 bg-red-900/30 border-red-700';
      default:
        return 'text-slate-400 bg-slate-900/30 border-slate-700';
    }
  };

  // Premium-locked state - Show compelling FOMO upgrade UI
  if (!isProfessional) {
    return (
      <div className="space-y-8">
        {/* Hero Section with FOMO */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-900/40 via-slate-900 to-emerald-900/40 border border-purple-500/30">
          {/* Animated background particles */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-10 left-10 w-32 h-32 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-10 right-10 w-40 h-40 bg-emerald-500/20 rounded-full blur-3xl animate-pulse delay-700" />
            <div className="absolute top-1/2 left-1/2 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
          </div>

          <div className="relative p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-4">
                  <Lock className="w-5 h-5 text-purple-400" />
                  <span className="text-purple-400 font-mono text-sm uppercase tracking-wider">Professional Feature</span>
                </div>

                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  AI Portfolio Project Generator
                  <Sparkles className="inline-block w-8 h-8 ml-2 text-yellow-400 animate-pulse" />
                </h2>

                <p className="text-lg text-slate-300 mb-6 max-w-xl">
                  Get <span className="text-emerald-400 font-semibold">personalized project ideas</span> with complete implementation guides,
                  GitHub repo structures, and tech stacks tailored to your career path.
                </p>

                {/* Social Proof */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex -space-x-2">
                    {[1, 2, 3, 4, 5].map(i => (
                      <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 border-2 border-slate-900 flex items-center justify-center text-xs font-bold text-white">
                        {['J', 'S', 'A', 'M', 'K'][i - 1]}
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-slate-400">
                    <span className="text-white font-semibold">2,847+ professionals</span> built stunning portfolios this month
                  </p>
                </div>

                {/* Feature highlights */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                  {[
                    { icon: Rocket, text: "5+ unique project ideas per career" },
                    { icon: GitBranch, text: "Complete GitHub repo structure" },
                    { icon: Code, text: "Tech stack recommendations" },
                    { icon: Book, text: "Step-by-step implementation guide" }
                  ].map((feature, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
                      <feature.icon className="w-4 h-4 text-emerald-400" />
                      <span>{feature.text}</span>
                    </div>
                  ))}
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => navigate('/subscribe')}
                    className="group relative px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl font-bold text-white text-lg shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all hover:scale-105"
                  >
                    <span className="flex items-center justify-center gap-2">
                      <Star className="w-5 h-5" />
                      Upgrade to Professional
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </button>

                  <div className="flex items-center gap-2 text-slate-400 text-sm">
                    <TrendingUp className="w-4 h-4 text-emerald-400" />
                    <span>Most popular among developers</span>
                  </div>
                </div>
              </div>

              {/* Preview Mock */}
              <div className="hidden lg:block w-80 flex-shrink-0">
                <div className="relative">
                  {/* Blur overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent z-10" />

                  <div className="bg-slate-800/80 backdrop-blur border border-slate-700 rounded-xl p-4 transform rotate-2 shadow-2xl">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      <div className="w-3 h-3 rounded-full bg-yellow-500" />
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                    </div>
                    <div className="space-y-2">
                      <div className="h-4 bg-emerald-500/30 rounded w-3/4" />
                      <div className="h-3 bg-slate-700 rounded w-full" />
                      <div className="h-3 bg-slate-700 rounded w-5/6" />
                      <div className="flex gap-2 mt-4">
                        <div className="h-6 bg-blue-500/30 rounded px-2 text-xs text-blue-400 flex items-center">React</div>
                        <div className="h-6 bg-green-500/30 rounded px-2 text-xs text-green-400 flex items-center">Node.js</div>
                        <div className="h-6 bg-purple-500/30 rounded px-2 text-xs text-purple-400 flex items-center">MongoDB</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-800/80 backdrop-blur border border-slate-700 rounded-xl p-4 transform -rotate-1 -mt-4 ml-4 shadow-2xl">
                    <div className="space-y-2">
                      <div className="h-4 bg-purple-500/30 rounded w-2/3" />
                      <div className="h-3 bg-slate-700 rounded w-full" />
                      <div className="h-3 bg-slate-700 rounded w-4/5" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Urgency Banner */}
        <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-red-500/10 border border-amber-500/30 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
              <Zap className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-white font-semibold">Limited Time: 40% off Professional Plan</p>
              <p className="text-slate-400 text-sm">Join 10,000+ professionals accelerating their careers</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/subscribe')}
            className="px-6 py-2 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-lg transition-colors whitespace-nowrap"
          >
            Claim Offer
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="w-16 h-16 text-emerald-500 animate-spin mb-4" />
        <p className="text-xl text-white font-semibold">Generating portfolio projects...</p>
        <p className="text-slate-400 mt-2">AI is crafting personalized project ideas for you</p>
      </div>
    );
  }

  if (loadingRoadmaps) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mb-4" />
        <p className="text-lg text-white">Loading your roadmaps...</p>
      </div>
    );
  }

  // Show CTA if no roadmaps exist
  if (roadmaps.length === 0) {
    return (
      <TechCard className="p-12 text-center">
        <Target className="w-20 h-20 text-slate-600 mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-white mb-2">No Roadmaps Yet</h3>
        <p className="text-slate-400 max-w-md mx-auto mb-6">
          Create a career roadmap first to unlock personalized portfolio project ideas tailored to your career path.
        </p>
        <TechButton
          onClick={() => navigate('/free-results')}
          icon={ArrowRight}
          className="text-lg px-8"
        >
          Create Your First Roadmap
        </TechButton>
      </TechCard>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <TechCard className="p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">Portfolio Project Generator</h2>
            <p className="text-slate-400">
              AI-generated project ideas with implementation guides and GitHub structure
            </p>
          </div>
          <Rocket className="w-12 h-12 text-emerald-400" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="block text-white font-semibold mb-2">Select From Your Roadmaps</label>
            <select
              value={selectedCareerPath}
              onChange={(e) => setSelectedCareerPath(e.target.value)}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">-- Choose a roadmap --</option>
              {roadmaps.map((roadmap, index) => (
                <option key={roadmap.id || index} value={roadmap.careerPath}>
                  {roadmap.careerPath}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <TechButton
              onClick={handleGenerateProjects}
              disabled={!selectedCareerPath || loading}
              icon={RefreshCw}
              className="w-full text-lg py-3"
            >
              Generate Projects
            </TechButton>
          </div>
        </div>
      </TechCard>

      {/* Limit Error - Show Upgrade Prompt */}
      {limitError && (
        <UpgradePrompt
          title="Portfolio Project Limit Reached"
          message={limitError.message || "You've reached your portfolio project generation limit. Upgrade to continue generating projects."}
          feature="Portfolio Projects"
          limit={limitError.limit}
          used={limitError.used}
        />
      )}

      {/* Projects Grid */}
      {projects.length > 0 && !limitError && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-2xl font-bold text-white">Your Portfolio Projects</h3>
            <TechBadge type="primary">{projects.length} Projects</TechBadge>
          </div>

          {projects.map((project, index) => (
            <TechCard key={index} className="p-6 hover:border-emerald-500/50 transition-all">
              <div
                className="cursor-pointer"
                onClick={() => toggleProject(index)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-grow">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-2xl font-bold text-white">{project.title}</h3>
                      <div className={`px-3 py-1 rounded-lg border ${getDifficultyColor(project.difficulty)}`}>
                        <span className="text-sm font-bold">{project.difficulty}</span>
                      </div>
                    </div>
                    <p className="text-slate-400 leading-relaxed">{project.description}</p>
                  </div>
                  {expandedProjects[index] ? (
                    <ChevronUp className="w-6 h-6 text-slate-400 ml-4 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-6 h-6 text-slate-400 ml-4 flex-shrink-0" />
                  )}
                </div>

                {/* Tech Stack Preview */}
                <div className="flex flex-wrap gap-2 mt-4">
                  {project.tech_stack?.slice(0, expandedProjects[index] ? undefined : 5).map((tech, i) => (
                    <TechBadge key={i}>{tech}</TechBadge>
                  ))}
                  {!expandedProjects[index] && project.tech_stack?.length > 5 && (
                    <TechBadge>+{project.tech_stack.length - 5} more</TechBadge>
                  )}
                </div>
              </div>

              {/* Expanded Content */}
              {expandedProjects[index] && (
                <div className="mt-6 space-y-6 border-t border-slate-700 pt-6">
                  {/* Full Tech Stack */}
                  <div>
                    <h4 className="text-xl font-semibold text-emerald-400 mb-3 flex items-center">
                      <Layers className="w-5 h-5 mr-2" />
                      Technology Stack
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {project.tech_stack?.map((tech, i) => (
                        <TechBadge key={i} type="primary">{tech}</TechBadge>
                      ))}
                    </div>
                  </div>

                  {/* GitHub Repository Structure */}
                  {project.github_structure && (
                    <div>
                      <h4 className="text-xl font-semibold text-emerald-400 mb-3 flex items-center">
                        <GitBranch className="w-5 h-5 mr-2" />
                        GitHub Repository Structure
                      </h4>
                      <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 font-mono text-sm">
                        {Object.entries(project.github_structure).map(([folder, contents], i) => (
                          <div key={i} className="mb-3">
                            <div className="flex items-center space-x-2 mb-1">
                              <Folder className="w-4 h-4 text-yellow-400" />
                              <span className="text-emerald-400 font-bold">{folder}</span>
                            </div>
                            <div className="ml-6 space-y-1">
                              {Array.isArray(contents) ? (
                                contents.map((file, j) => (
                                  <div key={j} className="flex items-center space-x-2">
                                    <Code className="w-3 h-3 text-slate-500" />
                                    <span className="text-slate-300">{file}</span>
                                  </div>
                                ))
                              ) : typeof contents === 'object' ? (
                                Object.entries(contents).map(([subfolder, subfiles], k) => (
                                  <div key={k} className="ml-4">
                                    <div className="flex items-center space-x-2 mb-1">
                                      <Folder className="w-3 h-3 text-yellow-400" />
                                      <span className="text-blue-400">{subfolder}</span>
                                    </div>
                                    <div className="ml-6 space-y-1">
                                      {Array.isArray(subfiles) && subfiles.map((file, l) => (
                                        <div key={l} className="flex items-center space-x-2">
                                          <Code className="w-3 h-3 text-slate-500" />
                                          <span className="text-slate-300">{file}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <span className="text-slate-300">{contents}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Implementation Guide */}
                  {project.implementation_guide && (
                    <div>
                      <h4 className="text-xl font-semibold text-emerald-400 mb-3 flex items-center">
                        <Book className="w-5 h-5 mr-2" />
                        Implementation Guide
                      </h4>
                      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                        {Array.isArray(project.implementation_guide) ? (
                          <ol className="space-y-4">
                            {project.implementation_guide.map((step, i) => (
                              <li key={i} className="flex items-start space-x-3">
                                <div className="bg-emerald-500 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                                  {i + 1}
                                </div>
                                <p className="text-white leading-relaxed pt-1">{typeof step === 'string' ? step : JSON.stringify(step)}</p>
                              </li>
                            ))}
                          </ol>
                        ) : typeof project.implementation_guide === 'object' ? (
                          <div className="space-y-6">
                            {Object.entries(project.implementation_guide).map(([phase, content], i) => (
                              <div key={phase} className="border-l-2 border-emerald-500 pl-4">
                                <h5 className="text-lg font-semibold text-emerald-400 mb-2 capitalize">
                                  {phase.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
                                </h5>
                                {Array.isArray(content) ? (
                                  <ul className="space-y-2">
                                    {content.map((item, j) => (
                                      <li key={j} className="text-white text-sm flex items-start">
                                        <span className="text-emerald-400 mr-2">•</span>
                                        {typeof item === 'string' ? item : JSON.stringify(item)}
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <p className="text-white leading-relaxed">{typeof content === 'string' ? content : JSON.stringify(content)}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-white leading-relaxed whitespace-pre-line">{String(project.implementation_guide)}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Key Features */}
                  {project.key_features && (
                    <div>
                      <h4 className="text-xl font-semibold text-emerald-400 mb-3 flex items-center">
                        <Zap className="w-5 h-5 mr-2" />
                        Key Features to Implement
                      </h4>
                      {Array.isArray(project.key_features) ? (
                        <ul className="space-y-2">
                          {project.key_features.map((feature, i) => (
                            <li key={i} className="flex items-start space-x-3">
                              <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                              <span className="text-white leading-relaxed">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-white leading-relaxed whitespace-pre-line">{project.key_features}</p>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-4 pt-4 border-t border-slate-700">
                    <TechButton
                      onClick={() => toast.success('GitHub template creation coming soon!')}
                      icon={GitBranch}
                      className="flex-grow md:flex-grow-0"
                    >
                      Create GitHub Repo
                    </TechButton>
                    <TechButton
                      onClick={() => toast.success('Starter code generation coming soon!')}
                      icon={Code}
                      className="flex-grow md:flex-grow-0 bg-slate-700 hover:bg-slate-600"
                    >
                      Generate Starter Code
                    </TechButton>
                    <TechButton
                      onClick={() => toast.success('Resources link copied!')}
                      icon={ExternalLink}
                      className="flex-grow md:flex-grow-0 bg-slate-700 hover:bg-slate-600"
                    >
                      View Resources
                    </TechButton>
                  </div>
                </div>
              )}
            </TechCard>
          ))}
        </div>
      )}

      {/* Empty State - Show Saved Portfolios or Prompt */}
      {projects.length === 0 && !loading && (
        <div className="space-y-6">
          {/* Saved Portfolios */}
          {savedPortfolios.length > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold text-white">Your Saved Portfolios</h3>
                <TechBadge type="primary">{savedPortfolios.length} Saved</TechBadge>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {savedPortfolios.map((portfolio) => (
                  <TechCard
                    key={portfolio.id}
                    className="p-6 cursor-pointer hover:border-emerald-500/50 transition-all group"
                    onClick={() => handleViewPortfolio(portfolio)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-white mb-1 group-hover:text-emerald-400 transition-colors">
                          {portfolio.careerPath}
                        </h4>
                        <p className="text-slate-400 text-sm mb-3">
                          {portfolio.projectCount || portfolio.projects?.length || 0} project ideas
                        </p>
                        <div className="text-xs text-slate-500">
                          Created {portfolio.createdAt?.toDate ?
                            new Date(portfolio.createdAt.toDate()).toLocaleDateString() :
                            'recently'}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => handleDeletePortfolio(portfolio.id, e)}
                          className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Delete portfolio"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                        <ArrowRight className="w-5 h-5 text-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  </TechCard>
                ))}
              </div>
            </div>
          ) : (
            <TechCard className="p-12 text-center">
              <Rocket className="w-20 h-20 text-slate-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">No Projects Generated Yet</h3>
              <p className="text-slate-400 max-w-md mx-auto">
                Select your target career path and click "Generate Projects" to receive AI-powered portfolio project ideas with complete implementation guides.
              </p>
            </TechCard>
          )}
        </div>
      )}
    </div>
  );
};

export default PortfolioBuilder;

